import type { Language } from "./languages";
import { de } from "./de";
import { pt } from "./pt";
import { es } from "./es";
import { ar } from "./ar";
import { it } from "./it";
import { zhCN } from "./zh-CN";
import { zhTW } from "./zh-TW";
import { ko } from "./ko";

const en = {
  "meta.title": "AgentBuck - Compare AI Model Pricing",
  "meta.description": "Compare API pricing for leading AI models and estimate monthly costs from your actual token usage.",
  "nav.prices": "Model pricing",
  "nav.calculator": "Cost calculator",
  "nav.providers": "Providers",
  "nav.guide": "Pricing guide",
  "nav.method": "Methodology",
  "aria.home": "AgentBuck home",
  "aria.language": "Select language",
  "aria.navigation": "Main navigation",
  "aria.breadcrumb": "Breadcrumb",
  "aria.dark": "Switch to dark mode",
  "aria.light": "Switch to light mode",
  "title.dark": "Dark mode",
  "title.light": "Light mode",
  "hero.eyebrow": "AI API pricing database",
  "hero.title": "Find the right AI model for your budget",
  "hero.subtitle": "Compare input, output, and cache pricing on one basis, then estimate monthly cost with your real token usage.",
  "stats.models": "Comparable models",
  "stats.providers": "Major providers",
  "stats.updated": "Data refreshed",
  "common.loading": "Loading",
  "notice.snapshot": "Live data is unavailable. Showing the built-in snapshot from Sep 7, 2026.",
  "common.retry": "Retry",
  "common.home": "Home",
  "common.models": "Models",
  "common.providers": "Providers",
  "common.from": "From",
  "common.viewAll": "View all",
  "common.backPricing": "Back to pricing",
  "share.quick": "Share this page",
  "share.kicker": "Share with your team",
  "share.title": "Share this comparison",
  "share.description": "Send this page with its exact models and usage assumptions.",
  "share.copy": "Copy link",
  "share.copied": "Link copied",
  "share.more": "Share",
  "share.openComparison": "Open share page",
  "share.email": "Email",
  "calculator.title": "Monthly cost calculator",
  "calculator.input": "Monthly input",
  "calculator.output": "Monthly output",
  "calculator.cache": "Cached input share",
  "calculator.million": "million tokens",
  "calculator.cheapest": "5 lowest-cost models now",
  "calculator.perMonth": "Estimate / mo",
  "prices.title": "Model pricing",
  "prices.subtitle": "Standard real-time API, USD per million tokens",
  "filter.open": "Filters",
  "filter.search": "Search models or providers",
  "filter.clearSearch": "Clear search",
  "filter.provider": "Filter by provider",
  "filter.allProviders": "All providers",
  "filter.sort": "Sort models",
  "filter.favorite": "Saved",
  "sort.monthly": "Estimated monthly cost",
  "sort.input": "Input price",
  "sort.output": "Output price",
  "sort.context": "Context window",
  "sort.newest": "Newest release",
  "cap.reasoning": "Reasoning",
  "cap.tools": "Tool calling",
  "cap.vision": "Vision",
  "cap.structured": "Structured output",
  "cap.openWeights": "Open weights",
  "results.found": "{count} models found",
  "results.source": "Rates are normalized from published pricing. Open provider docs to verify.",
  "column.compare": "Compare",
  "column.model": "Model / provider",
  "column.input": "Input",
  "column.cached": "Cached input",
  "column.output": "Output",
  "column.monthly": "Est. monthly",
  "column.context": "Context",
  "column.capabilities": "Reason  Tools  Vision",
  "column.actions": "Actions",
  "action.officialDocs": "Official docs",
  "action.addCompare": "Add to compare",
  "action.removeCompare": "Remove from compare",
  "action.addFavorite": "Save model",
  "action.removeFavorite": "Remove saved model",
  "action.added": "Added",
  "context.suffix": "context",
  "empty.title": "No matching models",
  "empty.description": "Try another search or clear the capability filters.",
  "empty.clear": "Clear filters",
  "home.exploreKicker": "Explore the catalog",
  "home.exploreTitle": "More ways to compare AI models",
  "home.providersTitle": "Browse by provider",
  "home.providersBody": "Compare every supported model from OpenAI, Anthropic, Google, DeepSeek, Alibaba, and more.",
  "home.guideTitle": "Read the pricing guide",
  "home.guideBody": "Understand token mix, prompt caching, context tiers, and what changes the final API bill.",
  "home.open": "Open",
  "providers.kicker": "Provider directory",
  "providers.title": "AI model providers",
  "providers.intro": "Browse normalized API pricing, context limits, and capabilities for every major provider in the AgentBuck catalog.",
  "providers.models": "{count} models",
  "providers.starting": "Lowest input rate",
  "providers.view": "View provider",
  "provider.kicker": "Provider pricing",
  "provider.titleSuffix": "API pricing",
  "provider.description": "Compare {count} {name} models by input price, output price, context window, and production capabilities.",
  "provider.cheapest": "Lowest input",
  "provider.largestContext": "Largest context",
  "provider.updated": "Catalog refreshed",
  "model.kicker": "Model pricing detail",
  "model.title": "{name} API pricing",
  "model.descriptionFallback": "Compare this model's API token rates, context limits, capabilities, and estimated monthly cost.",
  "model.input": "Input / 1M tokens",
  "model.cache": "Cached input / 1M",
  "model.output": "Output / 1M tokens",
  "model.context": "Context window",
  "model.maxOutput": "Maximum output",
  "model.released": "Released",
  "model.updated": "Model updated",
  "model.estimateTitle": "Your monthly estimate",
  "model.estimateBody": "Based on {input}M input, {output}M output, and {cache}% cached input.",
  "model.relatedTitle": "More models from {provider}",
  "compare.pageTitle": "Shared AI model cost comparison",
  "compare.pageBody": "Compare token rates, context windows, capabilities, and monthly cost using the assumptions saved in this URL.",
  "compare.emptyTitle": "No models in this comparison",
  "compare.emptyBody": "Choose two or three models from the pricing table to create a shareable comparison.",
  "compare.start": "Choose models",
  "compare.usage": "Shared workload",
  "notFound.title": "Page not found",
  "notFound.body": "The page may have moved, or the model is no longer in the current catalog.",
  "guide.kicker": "Practical buying guide",
  "guide.title": "Compare AI API pricing for the workload you actually run",
  "guide.intro": "The cheapest input rate is not always the lowest total bill. AgentBuck combines prompt, cached prompt, and generated-token costs so GPT, Claude, Gemini, DeepSeek, Qwen, Grok, and other models can be evaluated on the same workload.",
  "guide.inputTitle": "Start with your token mix",
  "guide.inputBody": "Chatbots usually consume more input than output. Coding agents and reasoning systems can generate much more output, which changes the best-value model.",
  "guide.cacheTitle": "Include prompt caching",
  "guide.cacheBody": "Repeated system prompts and long shared context can make cache-read pricing decisive. Set a realistic cache share before comparing monthly totals.",
  "guide.fitTitle": "Check capability and context",
  "guide.fitBody": "Price only matters if the model supports your context window, vision input, reasoning depth, structured output, and tool-calling requirements.",
  "faq.kicker": "Frequently asked questions",
  "faq.title": "AI model pricing FAQ",
  "faq.q1": "How does AgentBuck compare AI model API prices?",
  "faq.a1": "AgentBuck normalizes standard real-time API rates to US dollars per one million tokens, then separates input, cached input, and output costs. Provider documentation is linked for verification.",
  "faq.q2": "Which AI model has the cheapest API?",
  "faq.a2": "There is no universal cheapest model. The answer depends on your input-to-output ratio, cache usage, and required capabilities. Enter your monthly usage above to rank models for your workload.",
  "faq.q3": "How is the estimated monthly LLM cost calculated?",
  "faq.a3": "The estimate multiplies uncached input, cached input, and output volumes by each model’s corresponding rate. Taxes, batch discounts, regional pricing, and long-context surcharges are excluded.",
  "faq.q4": "Why can a model show a zero-dollar price?",
  "faq.a4": "Some providers publish free or promotional access for specific models. Availability and limits can change, so verify the linked provider documentation before production use.",
  "faq.q5": "How current is the pricing data?",
  "faq.a5": "AgentBuck refreshes its structured pricing catalog every six hours. The model table shows the newest upstream model update, while actual billing remains subject to provider terms.",
  "method.title": "Data and methodology",
  "method.priceTitle": "Pricing basis",
  "method.priceBody": "Rates are standard real-time API prices in USD per million tokens. Taxes, batch rates, regional uplifts, and long-context tiers are excluded.",
  "method.sourceTitle": "Data source",
  "method.sourceBefore": "Published model catalogs and provider pricing pages are normalized into one comparable dataset.",
  "method.sourceAfter": "AgentBuck refreshes it every six hours and links provider documentation for verification.",
  "method.costTitle": "Cost estimate",
  "method.costBody": "Monthly cost = regular input + cached input + output. Use it for budgeting; your provider dashboard is the billing source of truth.",
  "footer.tagline": "Make every token count.",
  "footer.updated": "Data refreshed {date}",
  "compare.selected": "Selected {count}/3",
  "compare.remove": "Remove {name}",
  "compare.button": "Compare models",
  "compare.kicker": "Model comparison",
  "compare.title": "See price and capabilities side by side",
  "compare.close": "Close",
  "compare.input": "Input / MTok",
  "compare.cache": "Cache / MTok",
  "compare.output": "Output / MTok",
  "compare.monthly": "Est. monthly cost",
  "compare.context": "Context",
  "compare.capabilities": "Reasoning / tools / vision",
  "compare.docs": "Provider docs",
  "compare.viewPrice": "View pricing",
  "nav.requestCalculator": "Request cost calculator",
  "home.calculatorTitle": "Estimate cost by request volume",
  "home.calculatorBody": "Calculate monthly spend from daily requests, tokens per request, and cache-hit rate.",
  "home.compareDirectoryTitle": "Browse model comparisons",
  "home.compareDirectoryBody": "Start from a curated comparison or build your own from the pricing table.",
  "model.formulaTitle": "Cost formula",
  "model.formulaBody": "Monthly cost = (uncached input tokens × input rate) + (cached input tokens × cache-read rate) + (output tokens × output rate), all per one million tokens. Without a published cache-read rate, cached tokens are estimated at the standard input rate.",
  "model.exampleTitle": "Reproducible example",
  "model.exampleBody": "Example: {inputM} input tokens ({cachePercent}% served from cache) and {outputM} output tokens cost {total} at this model's published rates — {inputRate} input, {cacheRate} cached input, {outputRate} output per 1M tokens.",
  "model.calculatorCta": "Estimate cost by daily request volume",
  "model.comparisonsTitle": "Featured in these curated comparisons",
} as const;

export type TranslationKey = keyof typeof en;
export type Dictionary = Record<TranslationKey, string>;

const ja: Dictionary = {
  ...en,
  "meta.title": "AgentBuck - AIモデル料金比較",
  "meta.description": "主要AIモデルのAPI料金を比較し、実際のトークン使用量から月額コストを見積もります。",
  "nav.prices": "モデル料金",
  "nav.calculator": "コスト計算",
  "nav.providers": "プロバイダー",
  "nav.guide": "料金ガイド",
  "nav.method": "データについて",
  "aria.home": "AgentBuck ホーム",
  "aria.language": "言語を選択",
  "aria.navigation": "メインナビゲーション",
  "aria.breadcrumb": "パンくずリスト",
  "aria.dark": "ダークモードに切り替え",
  "aria.light": "ライトモードに切り替え",
  "title.dark": "ダークモード",
  "title.light": "ライトモード",
  "hero.eyebrow": "AI API 料金データベース",
  "hero.title": "予算に合うAIモデルを見つける",
  "hero.subtitle": "入力・出力・キャッシュ料金を同じ基準で比較し、実際のトークン使用量から月額コストを見積もります。",
  "stats.models": "比較可能モデル",
  "stats.providers": "主要プロバイダー",
  "stats.updated": "データ更新",
  "common.loading": "読み込み中",
  "notice.snapshot": "リアルタイムデータを取得できません。2026年9月7日の内蔵スナップショットを表示しています。",
  "common.retry": "再試行",
  "common.home": "ホーム",
  "common.models": "モデル",
  "common.providers": "プロバイダー",
  "common.from": "最低",
  "common.viewAll": "すべて見る",
  "common.backPricing": "料金表に戻る",
  "share.quick": "このページを共有",
  "share.kicker": "チームに共有",
  "share.title": "この比較を共有",
  "share.description": "選択モデルと利用条件を含むページをそのまま共有できます。",
  "share.copy": "リンクをコピー",
  "share.copied": "コピーしました",
  "share.more": "共有",
  "share.openComparison": "共有ページを開く",
  "share.email": "メール",
  "calculator.title": "月額コスト計算",
  "calculator.input": "月間入力",
  "calculator.output": "月間出力",
  "calculator.cache": "キャッシュ入力比率",
  "calculator.million": "百万 tokens",
  "calculator.cheapest": "現在最も低コストな5モデル",
  "calculator.perMonth": "月額見積もり",
  "prices.title": "モデル料金",
  "prices.subtitle": "標準リアルタイムAPI、USD / 百万tokens",
  "filter.open": "絞り込み",
  "filter.search": "モデルまたはプロバイダーを検索",
  "filter.clearSearch": "検索をクリア",
  "filter.provider": "プロバイダーで絞り込み",
  "filter.allProviders": "すべてのプロバイダー",
  "filter.sort": "並び順",
  "filter.favorite": "保存済み",
  "sort.monthly": "月額コスト見積もり",
  "sort.input": "入力料金",
  "sort.output": "出力料金",
  "sort.context": "コンテキスト長",
  "sort.newest": "新しいリリース",
  "cap.reasoning": "推論",
  "cap.tools": "ツール呼び出し",
  "cap.vision": "画像入力",
  "cap.structured": "構造化出力",
  "cap.openWeights": "オープンウェイト",
  "results.found": "{count}件のモデル",
  "results.source": "公開料金を共通基準に換算。公式料金はプロバイダー文書で確認できます。",
  "column.compare": "比較",
  "column.model": "モデル / プロバイダー",
  "column.input": "入力",
  "column.cached": "キャッシュ入力",
  "column.output": "出力",
  "column.monthly": "月額見積もり",
  "column.context": "コンテキスト",
  "column.capabilities": "推論  ツール  画像",
  "column.actions": "操作",
  "action.officialDocs": "公式文書",
  "action.addCompare": "比較に追加",
  "action.removeCompare": "比較から削除",
  "action.addFavorite": "保存する",
  "action.removeFavorite": "保存を解除",
  "action.added": "追加済み",
  "context.suffix": "コンテキスト",
  "empty.title": "一致するモデルがありません",
  "empty.description": "検索語を変更するか、機能フィルターを解除してください。",
  "empty.clear": "フィルターを解除",
  "home.exploreKicker": "カタログを見る",
  "home.exploreTitle": "AIモデルを比較するその他の方法",
  "home.providersTitle": "プロバイダー別に見る",
  "home.providersBody": "OpenAI、Anthropic、Google、DeepSeek、Alibabaなど、対応プロバイダーの全モデルを比較します。",
  "home.guideTitle": "料金ガイドを読む",
  "home.guideBody": "トークン比率、プロンプトキャッシュ、コンテキスト段階料金など、API請求額を左右する要素を解説します。",
  "home.open": "開く",
  "providers.kicker": "プロバイダー一覧",
  "providers.title": "AIモデルプロバイダー",
  "providers.intro": "主要プロバイダーのAPI料金、コンテキスト上限、機能を共通基準で確認できます。",
  "providers.models": "{count}モデル",
  "providers.starting": "最低入力料金",
  "providers.view": "プロバイダーを見る",
  "provider.kicker": "プロバイダー料金",
  "provider.titleSuffix": "API料金",
  "provider.description": "{name}の{count}モデルを入力・出力料金、コンテキスト、実運用機能で比較します。",
  "provider.cheapest": "最低入力料金",
  "provider.largestContext": "最大コンテキスト",
  "provider.updated": "カタログ更新",
  "model.kicker": "モデル料金詳細",
  "model.title": "{name} API料金",
  "model.descriptionFallback": "APIトークン料金、コンテキスト上限、機能、月額見積もりを確認できます。",
  "model.input": "入力 / 100万tokens",
  "model.cache": "キャッシュ入力 / 100万",
  "model.output": "出力 / 100万tokens",
  "model.context": "コンテキスト長",
  "model.maxOutput": "最大出力",
  "model.released": "リリース日",
  "model.updated": "モデル更新日",
  "model.estimateTitle": "月額見積もり",
  "model.estimateBody": "入力{input}M、出力{output}M、キャッシュ入力{cache}%で計算。",
  "model.relatedTitle": "{provider}のその他のモデル",
  "compare.pageTitle": "共有されたAIモデル料金比較",
  "compare.pageBody": "URLに保存された条件で、トークン料金、コンテキスト、機能、月額コストを比較します。",
  "compare.emptyTitle": "比較するモデルがありません",
  "compare.emptyBody": "料金表から2〜3モデルを選び、共有可能な比較を作成してください。",
  "compare.start": "モデルを選ぶ",
  "compare.usage": "共有ワークロード",
  "notFound.title": "ページが見つかりません",
  "notFound.body": "ページが移動したか、モデルが現在のカタログから削除された可能性があります。",
  "guide.kicker": "実践的な選び方",
  "guide.title": "実際のワークロードに合わせてAI API料金を比較",
  "guide.intro": "入力単価が最安でも、請求総額が最小とは限りません。AgentBuckはプロンプト、キャッシュ済みプロンプト、生成トークンの費用を合算し、GPT、Claude、Gemini、DeepSeek、Qwen、Grokなどを同じ条件で比較します。",
  "guide.inputTitle": "トークン比率から始める",
  "guide.inputBody": "チャットボットは一般に出力より入力が多く、コーディングエージェントや推論システムは出力が増えます。この違いで最適なモデルが変わります。",
  "guide.cacheTitle": "プロンプトキャッシュを含める",
  "guide.cacheBody": "繰り返すシステムプロンプトや長い共通コンテキストでは、キャッシュ読み取り料金が重要です。現実的なキャッシュ比率で月額を比較してください。",
  "guide.fitTitle": "機能とコンテキストを確認",
  "guide.fitBody": "必要なコンテキスト長、画像入力、推論、構造化出力、ツール呼び出しに対応して初めて、価格比較に意味があります。",
  "faq.kicker": "よくある質問",
  "faq.title": "AIモデル料金 FAQ",
  "faq.q1": "AgentBuckはAIモデルのAPI料金をどのように比較しますか？",
  "faq.a1": "標準リアルタイムAPI料金を百万トークンあたりの米ドルに統一し、入力、キャッシュ入力、出力を分けて比較します。確認用に各社の公式文書も掲載しています。",
  "faq.q2": "最も安いAIモデルAPIはどれですか？",
  "faq.a2": "常に最安のモデルはありません。入力と出力の比率、キャッシュ利用、必要機能で変わります。上の月間使用量を入力すると、実際の用途に合わせて並べ替えられます。",
  "faq.q3": "LLMの月額コストはどのように計算しますか？",
  "faq.a3": "通常入力、キャッシュ入力、出力の量に各モデルの単価を掛けて合算します。税、Batch割引、地域料金、長文コンテキスト加算は含みません。",
  "faq.q4": "料金が0ドルと表示されるモデルがあるのはなぜですか？",
  "faq.a4": "一部のプロバイダーは特定モデルを無料またはキャンペーン料金で提供しています。条件は変わるため、本番利用前にリンク先の公式文書を確認してください。",
  "faq.q5": "料金データはどのくらい新しいですか？",
  "faq.a5": "AgentBuckは構造化料金カタログを6時間ごとに更新します。表には最新のモデル更新日を表示しますが、請求には各社の規約が適用されます。",
  "method.title": "データと算定方法",
  "method.priceTitle": "料金基準",
  "method.priceBody": "標準リアルタイムAPIの百万トークンあたり米ドル料金です。税、Batch、地域加算、長文コンテキストの段階料金は含みません。",
  "method.sourceTitle": "データソース",
  "method.sourceBefore": "公開モデルカタログと各社の料金ページを、比較可能なデータセットに正規化しています。",
  "method.sourceAfter": "AgentBuckが6時間ごとに更新し、確認用に各社の公式文書を掲載しています。",
  "method.costTitle": "コスト見積もり",
  "method.costBody": "月額コスト = 通常入力 + キャッシュ入力 + 出力。予算比較用の目安であり、請求額は各社の管理画面をご確認ください。",
  "footer.tagline": "すべてのトークンを有効に。",
  "footer.updated": "データ更新: {date}",
  "compare.selected": "選択済み {count}/3",
  "compare.remove": "{name} を削除",
  "compare.button": "モデルを比較",
  "compare.kicker": "モデル比較",
  "compare.title": "料金と機能を並べて確認",
  "compare.close": "閉じる",
  "compare.input": "入力 / MTok",
  "compare.cache": "キャッシュ / MTok",
  "compare.output": "出力 / MTok",
  "compare.monthly": "月額コスト見積もり",
  "compare.context": "コンテキスト",
  "compare.capabilities": "推論 / ツール / 画像",
  "compare.docs": "公式文書",
  "compare.viewPrice": "料金を見る",
  "nav.requestCalculator": "リクエストコスト計算機",
  "home.calculatorTitle": "リクエスト量でコストを見積もる",
  "home.calculatorBody": "1日あたりのリクエスト数、リクエストごとのトークン数、キャッシュ命中率から月額コストを計算します。",
  "home.compareDirectoryTitle": "モデル比較を見る",
  "home.compareDirectoryBody": "厳選された比較から始めるか、料金表から独自の比較を作成できます。",
  "model.formulaTitle": "コスト計算式",
  "model.formulaBody": "月額コスト = (非キャッシュ入力トークン × 入力単価) + (キャッシュ入力トークン × キャッシュ読み取り単価) + (出力トークン × 出力単価)（すべて100万トークンあたり）。キャッシュ読み取り単価が公開されていない場合、キャッシュトークンは標準入力単価で見積もります。",
  "model.exampleTitle": "再現可能な例",
  "model.exampleBody": "例：入力{inputM}トークン（うち{cachePercent}%がキャッシュ）、出力{outputM}トークンの場合、このモデルの公開料金では{total}になります — 入力{inputRate}、キャッシュ入力{cacheRate}、出力{outputRate}（100万トークンあたり）。",
  "model.calculatorCta": "1日のリクエスト量でコストを見積もる",
  "model.comparisonsTitle": "この厳選比較に含まれています",
};

const fr: Dictionary = {
  ...en,
  "meta.title": "AgentBuck - Comparateur de prix des modèles IA",
  "meta.description": "Comparez les tarifs API des principaux modèles IA et estimez votre coût mensuel selon votre consommation réelle de tokens.",
  "nav.prices": "Tarifs des modèles",
  "nav.calculator": "Calculateur de coût",
  "nav.providers": "Fournisseurs",
  "nav.guide": "Guide tarifaire",
  "nav.method": "Méthodologie",
  "aria.home": "Accueil AgentBuck",
  "aria.language": "Choisir la langue",
  "aria.navigation": "Navigation principale",
  "aria.breadcrumb": "Fil d’Ariane",
  "aria.dark": "Activer le mode sombre",
  "aria.light": "Activer le mode clair",
  "title.dark": "Mode sombre",
  "title.light": "Mode clair",
  "hero.eyebrow": "Base de données des tarifs API IA",
  "hero.title": "Trouvez le modèle IA adapté à votre budget",
  "hero.subtitle": "Comparez les tarifs d’entrée, de sortie et de cache sur une même base, puis estimez le coût mensuel selon votre usage réel.",
  "stats.models": "Modèles comparables",
  "stats.providers": "Fournisseurs majeurs",
  "stats.updated": "Données actualisées",
  "common.loading": "Chargement",
  "notice.snapshot": "Les données en direct sont indisponibles. Affichage de l’instantané intégré du 7 septembre 2026.",
  "common.retry": "Réessayer",
  "common.home": "Accueil",
  "common.models": "Modèles",
  "common.providers": "Fournisseurs",
  "common.from": "À partir de",
  "common.viewAll": "Tout afficher",
  "common.backPricing": "Retour aux tarifs",
  "share.quick": "Partager cette page",
  "share.kicker": "Partager avec votre équipe",
  "share.title": "Partager ce comparatif",
  "share.description": "Envoyez cette page avec les modèles et hypothèses d’usage exacts.",
  "share.copy": "Copier le lien",
  "share.copied": "Lien copié",
  "share.more": "Partager",
  "share.openComparison": "Ouvrir la page partagée",
  "share.email": "E-mail",
  "calculator.title": "Calculateur de coût mensuel",
  "calculator.input": "Entrée mensuelle",
  "calculator.output": "Sortie mensuelle",
  "calculator.cache": "Part d’entrée en cache",
  "calculator.million": "million de tokens",
  "calculator.cheapest": "Les 5 modèles les moins chers",
  "calculator.perMonth": "Estimation / mois",
  "prices.title": "Tarifs des modèles",
  "prices.subtitle": "API standard en temps réel, USD par million de tokens",
  "filter.open": "Filtres",
  "filter.search": "Rechercher un modèle ou fournisseur",
  "filter.clearSearch": "Effacer la recherche",
  "filter.provider": "Filtrer par fournisseur",
  "filter.allProviders": "Tous les fournisseurs",
  "filter.sort": "Trier les modèles",
  "filter.favorite": "Enregistrés",
  "sort.monthly": "Coût mensuel estimé",
  "sort.input": "Prix d’entrée",
  "sort.output": "Prix de sortie",
  "sort.context": "Fenêtre de contexte",
  "sort.newest": "Plus récents",
  "cap.reasoning": "Raisonnement",
  "cap.tools": "Appels d’outils",
  "cap.vision": "Vision",
  "cap.structured": "Sortie structurée",
  "cap.openWeights": "Poids ouverts",
  "results.found": "{count} modèles trouvés",
  "results.source": "Tarifs publics normalisés. Consultez la documentation du fournisseur pour vérification.",
  "column.compare": "Comparer",
  "column.model": "Modèle / fournisseur",
  "column.input": "Entrée",
  "column.cached": "Entrée en cache",
  "column.output": "Sortie",
  "column.monthly": "Coût mensuel",
  "column.context": "Contexte",
  "column.capabilities": "Raison.  Outils  Vision",
  "column.actions": "Actions",
  "action.officialDocs": "Documentation",
  "action.addCompare": "Ajouter au comparatif",
  "action.removeCompare": "Retirer du comparatif",
  "action.addFavorite": "Enregistrer le modèle",
  "action.removeFavorite": "Retirer des favoris",
  "action.added": "Ajouté",
  "context.suffix": "de contexte",
  "empty.title": "Aucun modèle correspondant",
  "empty.description": "Essayez une autre recherche ou retirez les filtres de capacité.",
  "empty.clear": "Effacer les filtres",
  "home.exploreKicker": "Explorer le catalogue",
  "home.exploreTitle": "D’autres façons de comparer les modèles IA",
  "home.providersTitle": "Parcourir par fournisseur",
  "home.providersBody": "Comparez tous les modèles pris en charge d’OpenAI, Anthropic, Google, DeepSeek, Alibaba et plus.",
  "home.guideTitle": "Lire le guide tarifaire",
  "home.guideBody": "Comprenez la répartition des tokens, le cache de prompts, les paliers de contexte et les facteurs de facturation.",
  "home.open": "Ouvrir",
  "providers.kicker": "Annuaire des fournisseurs",
  "providers.title": "Fournisseurs de modèles IA",
  "providers.intro": "Consultez les tarifs API normalisés, les limites de contexte et les capacités des principaux fournisseurs du catalogue AgentBuck.",
  "providers.models": "{count} modèles",
  "providers.starting": "Tarif d’entrée minimal",
  "providers.view": "Voir le fournisseur",
  "provider.kicker": "Tarifs du fournisseur",
  "provider.titleSuffix": "Tarifs API",
  "provider.description": "Comparez {count} modèles {name} par prix d’entrée, prix de sortie, contexte et capacités de production.",
  "provider.cheapest": "Entrée la moins chère",
  "provider.largestContext": "Contexte maximal",
  "provider.updated": "Catalogue actualisé",
  "model.kicker": "Détail des tarifs du modèle",
  "model.title": "Tarifs API de {name}",
  "model.descriptionFallback": "Comparez les tarifs par token, le contexte, les capacités et le coût mensuel estimé de ce modèle.",
  "model.input": "Entrée / 1 M de tokens",
  "model.cache": "Entrée en cache / 1 M",
  "model.output": "Sortie / 1 M de tokens",
  "model.context": "Fenêtre de contexte",
  "model.maxOutput": "Sortie maximale",
  "model.released": "Date de sortie",
  "model.updated": "Mise à jour du modèle",
  "model.estimateTitle": "Votre estimation mensuelle",
  "model.estimateBody": "Calculée avec {input} M en entrée, {output} M en sortie et {cache}% d’entrée en cache.",
  "model.relatedTitle": "Autres modèles de {provider}",
  "compare.pageTitle": "Comparatif partagé des coûts IA",
  "compare.pageBody": "Comparez les tarifs, contextes, capacités et coûts mensuels avec les hypothèses enregistrées dans cette URL.",
  "compare.emptyTitle": "Aucun modèle dans ce comparatif",
  "compare.emptyBody": "Choisissez deux ou trois modèles dans le tableau pour créer un comparatif partageable.",
  "compare.start": "Choisir des modèles",
  "compare.usage": "Charge de travail partagée",
  "notFound.title": "Page introuvable",
  "notFound.body": "La page a peut-être été déplacée ou le modèle n’est plus présent dans le catalogue actuel.",
  "guide.kicker": "Guide pratique",
  "guide.title": "Comparez les tarifs des API IA pour votre charge réelle",
  "guide.intro": "Le tarif d’entrée le plus bas ne produit pas toujours la facture la plus faible. AgentBuck combine le coût des prompts, du cache et des tokens générés pour comparer GPT, Claude, Gemini, DeepSeek, Qwen, Grok et d’autres modèles sur une base identique.",
  "guide.inputTitle": "Commencez par votre répartition de tokens",
  "guide.inputBody": "Les chatbots consomment souvent plus d’entrée que de sortie. Les agents de code et systèmes de raisonnement génèrent davantage de sortie, ce qui change le meilleur choix.",
  "guide.cacheTitle": "Intégrez le cache de prompts",
  "guide.cacheBody": "Les prompts système répétés et les longs contextes partagés rendent le tarif de lecture du cache déterminant. Utilisez une part de cache réaliste.",
  "guide.fitTitle": "Vérifiez capacités et contexte",
  "guide.fitBody": "Le prix n’a de sens que si le modèle couvre votre fenêtre de contexte, la vision, le raisonnement, les sorties structurées et les appels d’outils.",
  "faq.kicker": "Questions fréquentes",
  "faq.title": "FAQ sur les tarifs des modèles IA",
  "faq.q1": "Comment AgentBuck compare-t-il les tarifs des API IA ?",
  "faq.a1": "AgentBuck normalise les tarifs standard en temps réel en dollars par million de tokens, puis sépare l’entrée, l’entrée en cache et la sortie. Les documentations officielles sont liées pour vérification.",
  "faq.q2": "Quelle API de modèle IA est la moins chère ?",
  "faq.a2": "Il n’existe pas de modèle toujours moins cher. Le résultat dépend du ratio entrée-sortie, du cache et des capacités requises. Saisissez votre usage mensuel ci-dessus pour obtenir un classement adapté.",
  "faq.q3": "Comment le coût mensuel d’un LLM est-il estimé ?",
  "faq.a3": "L’estimation multiplie les volumes d’entrée standard, d’entrée en cache et de sortie par les tarifs correspondants. Taxes, remises batch, tarifs régionaux et suppléments de contexte long sont exclus.",
  "faq.q4": "Pourquoi certains modèles affichent-ils un prix de zéro dollar ?",
  "faq.a4": "Certains fournisseurs proposent un accès gratuit ou promotionnel à des modèles précis. Les limites peuvent évoluer ; vérifiez la documentation officielle avant un usage en production.",
  "faq.q5": "Les données tarifaires sont-elles à jour ?",
  "faq.a5": "AgentBuck actualise son catalogue structuré toutes les six heures. Le tableau indique la dernière mise à jour des modèles, mais la facturation dépend toujours des conditions du fournisseur.",
  "method.title": "Données et méthodologie",
  "method.priceTitle": "Base tarifaire",
  "method.priceBody": "Tarifs des API standard en temps réel, en USD par million de tokens. Taxes, batch, surcoûts régionaux et paliers de contexte long sont exclus.",
  "method.sourceTitle": "Source des données",
  "method.sourceBefore": "Les catalogues publics et pages tarifaires des fournisseurs sont normalisés dans un jeu de données comparable.",
  "method.sourceAfter": "AgentBuck l’actualise toutes les six heures et lie les documentations officielles pour vérification.",
  "method.costTitle": "Estimation des coûts",
  "method.costBody": "Coût mensuel = entrée standard + entrée en cache + sortie. Cette estimation sert au budget ; le tableau de bord du fournisseur fait foi.",
  "footer.tagline": "Donnez de la valeur à chaque token.",
  "footer.updated": "Données actualisées le {date}",
  "compare.selected": "{count}/3 sélectionnés",
  "compare.remove": "Retirer {name}",
  "compare.button": "Comparer les modèles",
  "compare.kicker": "Comparatif de modèles",
  "compare.title": "Prix et capacités côte à côte",
  "compare.close": "Fermer",
  "compare.input": "Entrée / MTok",
  "compare.cache": "Cache / MTok",
  "compare.output": "Sortie / MTok",
  "compare.monthly": "Coût mensuel estimé",
  "compare.context": "Contexte",
  "compare.capabilities": "Raisonnement / outils / vision",
  "compare.docs": "Documentation",
  "compare.viewPrice": "Voir les tarifs",
  "nav.requestCalculator": "Calculateur de coût par requêtes",
  "home.calculatorTitle": "Estimer le coût par volume de requêtes",
  "home.calculatorBody": "Calculez la dépense mensuelle à partir des requêtes quotidiennes, des tokens par requête et du taux de succès du cache.",
  "home.compareDirectoryTitle": "Parcourir les comparatifs de modèles",
  "home.compareDirectoryBody": "Partez d’un comparatif sélectionné ou créez le vôtre depuis le tableau des tarifs.",
  "model.formulaTitle": "Formule de calcul du coût",
  "model.formulaBody": "Coût mensuel = (tokens d’entrée non mis en cache × tarif d’entrée) + (tokens d’entrée en cache × tarif de lecture du cache) + (tokens de sortie × tarif de sortie), le tout par million de tokens. Sans tarif de lecture du cache publié, les tokens en cache sont estimés au tarif d’entrée standard.",
  "model.exampleTitle": "Exemple reproductible",
  "model.exampleBody": "Exemple : {inputM} tokens d’entrée (dont {cachePercent}% servis depuis le cache) et {outputM} tokens de sortie coûtent {total} aux tarifs publiés de ce modèle — {inputRate} en entrée, {cacheRate} en entrée en cache, {outputRate} en sortie par million de tokens.",
  "model.calculatorCta": "Estimer le coût par volume de requêtes quotidien",
  "model.comparisonsTitle": "Présent dans ces comparatifs sélectionnés",
};

export const dictionaries: Record<Language, Dictionary> = { en, ja, fr, de, pt, es, ar, it, "zh-CN": zhCN, "zh-TW": zhTW, ko };

export function translate(language: Language, key: TranslationKey, values?: Record<string, string | number>) {
  let result: string = dictionaries[language][key];
  if (values) {
    for (const [name, value] of Object.entries(values)) {
      result = result.replaceAll(`{${name}}`, String(value));
    }
  }
  return result;
}
